import BackTo from 'components/BackTo';
import Error from 'components/form/Error';
import Input from 'components/form/Inputs/Input';
import { DefaultLoader, LoaderContainer } from 'components/Loader';
import { getConversationName } from 'components/messaging/utils';
import ActionModal from 'components/Modal/ActionModal';
import { PageHeadContainer, PageTitle } from 'components/PageHead';
import Space from 'components/Space';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import useSWR, { mutate } from 'swr';
import Conversation from 'types/Conversation';
import Message from 'types/Message';
import User from 'types/User';
import manageTextError from 'utils/manageTextError';
import privateAPI from 'utils/privateAxiosInstance';
import MessageComponent from './Message';
import MessageInput from './MessageInput';

const Container = styled.div`
  height: 100%;
  display: grid;
  grid-template-rows: min-content auto min-content;
`;

const MessagesContainer = styled.div`
  display: flex;
  flex-direction: column-reverse;
  overflow: hidden auto;
  margin-bottom: 10px;
  border-top: 1px solid var(--gray-50);
`;

const AcceptedExtensions = styled.p`
  color: var(--gray-500);
  font-size: 0.85rem;
`;

type Props = {
  url: string;
  backTo?: string;
  conversationsUrl?: string;
};
const ConversationComponent: FC<Props> = ({ url, backTo, conversationsUrl }) => {
  const { data: user }: { data?: User } = useSWR('/auth/profile');
  const { id } = useParams();

  const { data: conversation } = useSWR<Conversation>(url);
  const { data: conversations } = useSWR<Conversation[]>(
    conversationsUrl || null,
    { refreshInterval: 0 }
  );
  const messages = useMemo(() => conversation?.messages, [conversation]);

  // Scrolling to the bottom if a new message appears
  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollBottom = useCallback(
    () => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }),
    [bottomRef],
  );
  useEffect(() => scrollBottom(), [messages, scrollBottom]);

  // Updates the "unseen" bullet in the conversation list
  useEffect(() => {
    if (!conversationsUrl) return;
    mutate(
      conversationsUrl || '',
      [...(conversations || [])].map((c) => {
        if (id && c.id === +id && c.messages[0]) {
          c.messages[0].seen = true;
        }
        return c;
      }),
    );
  }, [conversations, id, conversationsUrl]);

  // Text message related states & methods
  const [typingMessage, setTypingMessage] = useState('');
  const [isSending, setIsSending] = useState(false);

  const handleTypingMessageChange: React.ChangeEventHandler<
    HTMLTextAreaElement
  > = (e) => {
    setTypingMessage(e.target.value);
  };

  const handleSubmit = () => {
    setIsSending(true);
    mutate<Conversation>(
      url,
      {
        ...conversation!,
        messages: [
          {
            id: -1,
            text: typingMessage,
            createdAt: new Date().toString(),
            senderId: user!.id || -1,
            sender: user,
            conversationId: +id!,
            conversation: conversation!,
          },
          ...messages!,
        ],
      },
      false,
    ).then(() => {
      scrollBottom();
      setTypingMessage('');
    });

    privateAPI
      .post<Message>(`/messaging/sendMessage`, {
        text: typingMessage,
        conversationId: conversation!.id,
      })
      .then((res) => {
        if (conversation && messages) {
          mutate<Conversation>(
            url,
            {
              ...conversation,
              messages: [res.data, ...messages],
            },
            true,
          );
        }
      })
      .catch((err) => {
        console.log(err);
        mutate<Conversation>(url);
        setTypingMessage(typingMessage);
      })
      .finally(() => setIsSending(false));
  };

  // File message related states & methods
  const [file, setFile] = useState<File | null>(null);
  const [fileOpen, setFileOpen] = useState<boolean>(false);
  const [fileError, setFileError] = useState<string>('');
  const [fileLoading, setFileLoading] = useState<boolean>(false);

  const handleChangeFile: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    return setFile(e.target.files?.length ? e.target.files[0] : null);
  };

  const handleFileSubmit = () => {
    setFileLoading(true);
    setFileError('');

    const formData = new FormData();
    formData.append('conversationId', conversation?.id?.toString() || '');
    formData.append('filename', file?.name || '');
    file && formData.append('file', file);

    privateAPI
      .post('/messaging/send-file-message', formData)
      .then(() => {
        setFileOpen(false);
        mutate(url);
      })
      .catch((err) => {
        manageTextError(err, setFileError);
      })
      .finally(() => {
        setFileLoading(false);
      });
  };

  return conversation ? (
    <>
      <ActionModal
        open={fileOpen}
        close={() => setFileOpen(false)}
        title="Envoyer un fichier"
        color="primary"
        apply={handleFileSubmit}
        applyText="Envoyer"
        loading={fileLoading}
      >
        {fileError && <Error>{fileError}</Error>}
        <Space height={0.5} />
        <Input
          type="file"
          onChange={handleChangeFile}
          accept=".png,.jpg,.jpeg,.pdf,.docx,.mp4"
          value={file?.name}
          style={{ width: '25rem', maxWidth: '100%' }}
        />
        <Space height={0.5} />
        <AcceptedExtensions>
          Extensions acceptées: .pdf, .png, .jpg, .docx, .mp4
          <br />
          Taille maximale: 30 Mo
        </AcceptedExtensions>
      </ActionModal>
      <Container>
        <PageHeadContainer>
          <PageTitle>
            {backTo && <BackTo backTo={backTo} />}
            <h1>
              {conversation &&
                (!!user?.id || user?.id === 0) &&
                getConversationName(conversation, user.id)}
            </h1>
          </PageTitle>
        </PageHeadContainer>
        <MessagesContainer>
          <div ref={bottomRef} />
          {messages?.map((m, idx) => {
            const fromMe = m.senderId === user?.id;
            return <MessageComponent key={idx} fromMe={fromMe} message={m} />;
          })}
        </MessagesContainer>
        <MessageInput
          value={typingMessage}
          onChange={handleTypingMessageChange}
          handleSubmit={handleSubmit}
          disabled={!conversation}
          submitting={isSending}
          openFileForm={() => setFileOpen(true)}
        />
      </Container>
    </>
  ) : (
    <LoaderContainer>
      <DefaultLoader size={1.5} />
    </LoaderContainer>
  );
};

export default ConversationComponent;
